import { ContentPaginatedInterface } from "./content.type";
import { CompanyResponse } from "./company.type";
import { FileResponse } from "./file.type";
import { MaterialResponse } from "./material.type";
import { SiteResponse } from "./site.type";
import { UserResponse } from "./user.type";

export enum PurchaseRequestStatus {
  DRAFT = "DRAFT",
  IN_APPROVAL = "IN_APPROVAL",
  QUOTING = "QUOTING",
  PARTIALLY_AWARDED = "PARTIALLY_AWARDED",
  AWARDED = "AWARDED",
  PENDING_DELIVERY = "PENDING_DELIVERY",
  DELIVERED = "DELIVERED",
  REJECTED = "REJECTED",
}

export enum PurchaseRequestItemState {
  DRAFT = "DRAFT",
  REJECTED = "REJECTED",
  IN_APPROVAL = "IN_APPROVAL",
  QUOTING = "QUOTING",
  AWARDED = "AWARDED",
  PENDING_DELIVERY = "PENDING_DELIVERY",
  DELIVERED = "DELIVERED",
}

export enum PurchaseRequestItemType {
  GOOD = "GOOD",
  SERVICE = "SERVICE",
}

export enum PurchaseRequestBudgetType {
  TOTAL = "TOTAL",
  PER_UNIT = "PER_UNIT",
}

export enum Currency {
  USD = "USD",
  EUR = "EUR",
  ARS = "ARS",
  BRL = "BRL",
  CLP = "CLP",
  COP = "COP",
  MXN = "MXN",
  PEN = "PEN",
  UYU = "UYU",
}

export const CURRENCY_OPTIONS: { value: Currency; label: string }[] = [
  { value: Currency.USD, label: "USD" },
  { value: Currency.EUR, label: "EUR" },
  { value: Currency.ARS, label: "ARS" },
  { value: Currency.BRL, label: "BRL" },
  { value: Currency.CLP, label: "CLP" },
  { value: Currency.COP, label: "COP" },
  { value: Currency.MXN, label: "MXN" },
  { value: Currency.PEN, label: "PEN" },
  { value: Currency.UYU, label: "UYU" },
];

export interface DocumentClass {
  code: string | null;
  description: string | null;
}

export interface PurchaseRequestItemV2 {
  id: string;
  request?: PurchaseRequestV2 | null;
  material?: MaterialResponse | null;
  state: PurchaseRequestItemState;
  itemType?: PurchaseRequestItemType | null;
  description: string | null;
  quantity: number | null;
  measurementUnit?: string | null;
  budgetValue: number | null;
  rejectedMessage?: string | null;
  currency: string | null;
  budgetCurrency?: string | null;
  purchaseRequestId: string;
  budgetType: PurchaseRequestBudgetType;
  assignedBuyer?: UserResponse | null;
  costCenter: string | null;
  neededFrom: string | null;
  neededTo: string | null;
  purpose?: string | null;
  brand?: string | null;
  address?: string | null;
  /** The group of the request the item belongs to — for an import, its requisition. */
  groupLabel?: string | null;
  site?: SiteResponse | null;
  spot?: boolean | null;
  aiValidated: boolean;
  source: string | null;
  active: boolean;
  deleted: boolean;
  createdAt: string;
  updatedAt: string;
  files?: PurchaseRequestFilePayload[];
  requestingSite?: SiteResponse | null;
}

export interface PurchaseRequestV2 {
  id: string;
  purchaseRequestCode: string | null;
  name?: string | null;
  rejectedMessage?: string | null;
  requester: UserResponse;
  /** The requester as an ERP sent it (SAP user code), set even when no Simbuy user matched. */
  requesterCode?: string | null;
  company: CompanyResponse;
  comments: string | null;
  originChannel: string | null;
  originMetadata: string | null;
  overallStatus: PurchaseRequestStatus;
  draft: boolean;
  active: boolean;
  deleted: boolean;
  items: PurchaseRequestItemV2[];
  files: PurchaseRequestFilePayload[];
  createdAt: string;
  updatedAt: string;
  documentClass?: DocumentClass | null;
}

export type PurchaseRequestFilePayload = {
  id?: string;
  clientId?: string;
  fileURL: string;
  name?: string;
  type: FileResponse["type"];
};

export type PurchaseRequestV2PaginatedResponse =
  ContentPaginatedInterface<PurchaseRequestV2>;

export type PurchaseRequestItemV2PaginatedResponse =
  ContentPaginatedInterface<PurchaseRequestItemV2>;

export type CreatePurchaseRequestItemV2Payload = {
  material?: { id: string } | null;
  itemType?: PurchaseRequestItemType;
  state?: PurchaseRequestItemState;
  title?: string | null;
  description?: string | null;
  quantity?: number | null;
  measurementUnit?: string | null;
  budgetValue?: number | null;
  budgetCurrency?: string | null;
  budgetType?: PurchaseRequestBudgetType;
  currency?: string | null;
  assignedBuyer?: { id: string } | null;
  site?: { id: string } | null;
  costCenter?: string | null;
  neededFrom?: string | null;
  neededTo?: string | null;
  purpose?: string | null;
  brand?: string | null;
  address?: string | null;
  aiValidated?: boolean;
  source?: string | null;
  files?: PurchaseRequestFilePayload[];
};

export type CreatePurchaseRequestV2Payload = Partial<
  Omit<
    PurchaseRequestV2,
    | "id"
    | "requester"
    | "company"
    | "overallStatus"
    | "items"
    | "active"
    | "deleted"
    | "createdAt"
    | "updatedAt"
    | "files"
  >
> & {
  items?: CreatePurchaseRequestItemV2Payload[];
  files?: PurchaseRequestFilePayload[];
};

// Backend Error Response Type
export interface BackendErrorResponse {
  timestamp: string; // ISO date string
  status: number; // HTTP status code
  error: string; // Error type like "Bad Request", "Internal Server Error"
  message: string; // Detailed error message
  path: string; // Request path
}

// Google Sheets Import Response (current backend implementation)
export interface GoogleSheetsImportResponse {
  message: string;
  status: string; // "processing"
}

export enum GoogleSheetsImportJobStatus {
  PENDING = "PENDING",
  PROCESSING = "PROCESSING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
}

// Google Sheets Import Job (for future polling implementation)
export interface GoogleSheetsImportJob {
  id: string;
  status: GoogleSheetsImportJobStatus;
  purchaseRequestId: string | null;
  itemsCount: number | null;
  errorMessage: string | null;
  spreadsheetUrl?: string;
  sheetName?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Paginated response for import jobs
export interface GoogleSheetsImportJobsResponse {
  content: GoogleSheetsImportJob[];
  totalPages: number;
  totalElements: number;
  size: number;
  number: number;
}
